import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Eye } from "lucide-react";

import { Field, TextInput, FormActions } from "../../components/FormField";
import { SEOSection } from "../../components/SEOSection";
import { ChangePartnerSections } from "./templates/ChangePartnerSections";

import {
  server_post_data,
  dynamic_page_save_update,
  get_dynamic_page_detail,
} from "../../serviceconnection/ServiceConnection";

import { showSuccess, showError, showSeoWarnings } from "../../lib/toast";

export function ChangePartnerCMS() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const [pageId, setPageId] = useState(0);
  const [pageTitle, setPageTitle] = useState("Change SAP Partner");
  const [slug, setSlug] = useState("/resources/change-sap-partner");

  const [seoData, setSeoData] = useState({});
  const handleSeoChange = (field, value) =>
    setSeoData((prev) => ({ ...prev, [field]: value }));

  const [sectionsData, setSectionsData] = useState({});
  const [initialSections, setInitialSections] = useState(null);

  const fetchPage = async () => {
    try {
      const listRes = await server_post_data(get_dynamic_page_detail, {
        main_id: 0,
      });
      const list = listRes.data?.data || [];
      const record = list.find((p) => p.page_type === "change_partner");
      if (!record) return;

      const detailRes = await server_post_data(get_dynamic_page_detail, {
        main_id: record.id,
      });
      const d = detailRes.data?.data || {};

      setPageId(d.id || 0);
      setPageTitle(d.page_name || "Change SAP Partner");
      setSlug(d.slug || "/resources/change-sap-partner");

      const sections = d.sections_data?.sections || d.sections_data || {};
      setInitialSections(sections);

      const seo = d.seo_data || {};
      setSeoData({
        seo_id: seo.primary_id || 0,
        actual_url: seo.actual_url || "",
        link_url: seo.link_url || "",
        canonical_url: seo.canonical_url || "",
        title_name: seo.title_name || "",
        favicon: seo.favicon || "",
        image: seo.image || "",
        description: seo.description || "",
        keywords: seo.keywords || "",
        pretty_function_name: seo.pretty_function_name || "ChangePartner",
        call_function_name:
          seo.call_function_name || "change_partner_page_detail",
        content_type: seo.content_type || "website",
        changefreq: seo.changefreq || "monthly",
        priority_screen: seo.priority_screen || "0.7",
      });
    } catch (err) {
      console.error("Failed to fetch change-partner page:", err);
    }
  };

  useEffect(() => {
    fetchPage();
  }, []);

  const handleSave = async (status) => {
    try {
      setLoading(true);
      const payload = {
        p_id: pageId,
        page_name: pageTitle,
        page_type: "change_partner",
        category: "",
        uuid: Date.now().toString(),
        seo_id: 0,
        url_call: slug,
        sections: sectionsData,
        seo: { seo_id: seoData.seo_id || 0, ...seoData },
      };
      const saveRes = await server_post_data(dynamic_page_save_update, payload);
      showSuccess(
        status === "published"
          ? "Page published successfully"
          : "Page saved successfully",
      );
      showSeoWarnings(saveRes);
      navigate("/pages")
    } catch (err) {
      console.error(err);
      showError("Error saving page");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-[900px] mx-auto space-y-5">
      <div className="page-header">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/pages")}
            className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-muted text-muted-foreground"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div>
            <h1 className="page-title">Change SAP Partner CMS</h1>
            <p className="page-subtitle">Edit change partner page sections</p>
          </div>
        </div>
        <button className="flex items-center gap-2 text-sm font-medium text-muted-foreground border border-border rounded-lg px-3 py-2 hover:bg-muted">
          <Eye className="w-4 h-4" /> Preview
        </button>
      </div>

      <div className="section-panel">
        <div className="section-panel-header">
          <h2 className="section-panel-title">Basic Information</h2>
        </div>
        <div className="section-panel-body space-y-4">
          <Field label="Page Title">
            <TextInput
              value={pageTitle}
              onChange={(e) => setPageTitle(e.target.value)}
            />
          </Field>
          <Field label="Slug">
              <TextInput value={slug} onChange={(e) => setSlug(e.target.value)}  disabled />
          </Field>
        </div>
      </div>

      <ChangePartnerSections
        setSectionsData={setSectionsData}
        initialData={initialSections}
      />

      <SEOSection
        seoData={seoData}
        onChange={handleSeoChange}
        defaults={{
          pretty_function_name: "ChangePartner",
          call_function_name: "change_partner_page_detail",
          priority_screen: "0.7",
        }}
      />

      <FormActions
        onCancel={() => navigate("/pages")}
        onSaveDraft={() => handleSave("draft")}
        onPublish={() => handleSave("published")}
        loading={loading}
      />
    </div>
  );
}
